import { useCallback } from 'react';
import { useApiMutation } from '@/hooks/useApi';
import chatWidgetApi from '@/api/chatWidgetApi';
import { useChatWidgetStore } from '@/stores/chatWidgetStore';
import { useLocales } from '@/locales';

const HISTORY_LIMIT = 20;

export function useChatSession() {
  const { translate, currentLang } = useLocales();

  const { mutateAsync, isPending } = useApiMutation(
    payload => chatWidgetApi.sendMessage(payload),
    { publishErrors: false },
  );

  const sendMessage = useCallback(
    async text => {
      const trimmed = text?.trim();
      if (!trimmed) return;

      const {
        messages,
        sessionId,
        contact,
        handoffActive,
        addMessage,
        setHandoffActive,
      } = useChatWidgetStore.getState();

      addMessage({ role: 'user', content: trimmed, createdAt: Date.now() });

      const history = [...messages, { role: 'user', content: trimmed }]
        .slice(-HISTORY_LIMIT)
        .map(m => ({ role: m.role, content: m.content }));

      try {
        const data = await mutateAsync({
          sessionId,
          message: trimmed,
          lang: currentLang.value,
          history,
          contact,
          pageUrl:
            typeof window !== 'undefined' ? window.location.pathname : '',
          handoffActive,
        });

        addMessage({
          role: 'assistant',
          content: data?.reply || translate('chat_handoff_fallback'),
          source: data?.source || 'ai',
          createdAt: Date.now(),
        });

        if (data?.handoff) {
          setHandoffActive(true);
        }
      } catch {
        addMessage({
          role: 'assistant',
          content: translate('chat_handoff_fallback'),
          source: 'telegram',
          createdAt: Date.now(),
        });
        setHandoffActive(true);
      }
    },
    [currentLang.value, mutateAsync, translate],
  );

  return {
    sendMessage,
    isSending: isPending,
  };
}
